<script>
    import { createEventDispatcher } from 'svelte';
    import Icon from '../icons/Icon.svelte';

    export let table;

    const dispatch = createEventDispatcher();

    function formatCurrency(val) {
        const num = parseFloat(val);
        return isNaN(num) ? '0,00' : num.toFixed(2).replace('.', ',');
    }

    function getTimeDiff(dateStr) {
        if (!dateStr) return '0 min';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '0 min';
        const mins = Math.max(1, Math.round((Date.now() - d.getTime()) / 60000));
        if (mins >= 60) {
            const hrs = Math.floor(mins / 60);
            const m = mins % 60;
            return `${hrs}h ${m}m`;
        }
        return `${mins} min`;
    }

    function handleOpenClick() {
        dispatch('openTable', table);
    }

    function handleDeleteClick(e) {
        if (e) e.stopPropagation();
        dispatch('deleteTable', { table, event: e });
    }

    function handleViewComanda() {
        dispatch('viewComanda', table);
    }

    function handleCloseTable() {
        dispatch('closeTable', table);
    }
</script>

<div class="table-card status-{table.status}" class:has-comanda={!!table.comanda}>
    <div class="table-card-header">
        <div class="table-number-group">
            <span class="table-number">#{String(table.table_number).padStart(2, '0')}</span>
            <span class="table-label" title={table.label}>{table.label}</span>
        </div>
        <span class="status-pill status-{table.status}">
            {#if table.status === 'available'}
                <span class="pill-dot green"></span> Livre
            {:else if table.status === 'occupied'}
                <span class="pill-dot blue"></span> Ocupada
            {:else if table.status === 'bill_requested'}
                <span class="pill-dot orange"></span> Conta Solicitada
            {:else}
                Reservada
            {/if}
        </span>
    </div>

    <div class="table-card-body">
        {#if table.status === 'available'}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div class="empty-table-state" on:click={handleOpenClick} title="Clique para abrir esta mesa">
                <div class="table-icon-empty">
                    <Icon name="utensils" size={28} />
                </div>
                <span class="capacity-text">
                    <Icon name="users" size={13} />
                    Capacidade: {table.seats} lugares
                </span>
                <span class="open-hint">Clique para abrir</span>
            </div>
        {:else if table.comanda}
            <div class="occupied-details">
                <div class="occupied-client-row">
                    <span class="client-name" title={table.comanda.customer_name || 'Cliente'}>
                        <Icon name="user" size={13} />
                        <strong>{table.comanda.customer_name || 'Cliente'}</strong>
                    </span>
                    <span class="people-badge" title="Pessoas na mesa">
                        <Icon name="users" size={12} />
                        {table.comanda.people_count || 1} pes.
                    </span>
                </div>

                <div class="occupied-meta-row">
                    <span class="time-elapsed" title="Tempo desde a abertura da comanda">
                        <Icon name="clock" size={12} />
                        {getTimeDiff(table.opened_at || table.comanda.opened_at)}
                    </span>
                    <span class="items-count-tag" title="Itens lançados">
                        <Icon name="shopping-bag" size={12} />
                        {(table.comanda.items || []).length} {(table.comanda.items || []).length === 1 ? 'item' : 'itens'}
                    </span>
                </div>

                {#if table.status === 'bill_requested'}
                    <div class="bill-requested-banner">
                        <Icon name="alert-triangle" size={12} />
                        <span>Conta solicitada</span>
                    </div>
                {/if}

                {#if (table.comanda.items || []).some(it => it.kitchenStatus === 'ready')}
                    <div class="bill-requested-banner ready">
                        <Icon name="check-circle" size={12} />
                        <span>Pedido pronto na cozinha!</span>
                    </div>
                {:else if (table.comanda.items || []).some(it => it.kitchenStatus === 'in_prep')}
                    <div class="bill-requested-banner in-prep">
                        <Icon name="clock" size={12} />
                        <span>Pedido em produção na cozinha</span>
                    </div>
                {/if}

                <div class="table-total-row">
                    <span class="total-label">Consumo:</span>
                    <span class="total-amount">R$ {formatCurrency(table.comanda.total || table.comanda.subtotal)}</span>
                </div>
            </div>
        {/if}
    </div>

    <div class="table-card-footer">
        {#if table.status === 'available'}
            <button class="btn-card-action open-btn" on:click={handleOpenClick}>
                <Icon name="plus" size={14} />
                <span>Abrir Mesa</span>
            </button>
            <button class="btn-card-delete" title="Excluir esta mesa" on:click|stopPropagation={handleDeleteClick}>
                <Icon name="trash" size={14} />
            </button>
        {:else}
            <button class="btn-card-action view-btn" on:click={handleViewComanda}>
                <Icon name="file-text" size={14} />
                <span>Comanda</span>
            </button>
            <button class="btn-card-action pay-btn" on:click={handleCloseTable}>
                <Icon name="check-circle" size={14} />
                <span>Fechar</span>
            </button>
        {/if}
    </div>
</div>

<style>
    .table-card {
        background: #FFFFFF;
        border: 1px solid var(--neutral-border, #E2E8F0);
        border-radius: var(--radius-lg, 16px);
        padding: 16px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        min-height: 200px;
        box-shadow: var(--shadow-card, 0 1px 3px rgba(15, 23, 42, 0.05));
        transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
        box-sizing: border-box;
    }

    .table-card:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-card-hover, 0 4px 12px rgba(15, 23, 42, 0.08));
        border-color: var(--neutral-border-focus, #CBD5E1);
    }

    .table-card.status-available {
        border: 1.5px solid #86EFAC;
        background: linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 60%);
        box-shadow: 0 2px 8px rgba(16, 185, 129, 0.08);
    }

    .table-card.status-available:hover {
        border-color: #22C55E;
        box-shadow: 0 6px 16px rgba(16, 185, 129, 0.16);
    }

    .table-card.status-occupied {
        border: 1.5px solid #93C5FD;
        background: linear-gradient(180deg, #EFF6FF 0%, #FFFFFF 60%);
        box-shadow: 0 2px 8px rgba(37, 99, 235, 0.08);
    }

    .table-card.status-occupied:hover {
        border-color: #3B82F6;
        box-shadow: 0 6px 16px rgba(37, 99, 235, 0.16);
    }

    .table-card.status-bill_requested {
        border: 1.5px solid #FCD34D;
        background: linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 60%);
        box-shadow: 0 2px 10px rgba(217, 119, 6, 0.14);
    }

    .table-card.status-bill_requested:hover {
        border-color: #F59E0B;
        box-shadow: 0 6px 18px rgba(217, 119, 6, 0.22);
    }

    .table-card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 12px;
    }

    .table-number-group {
        display: flex;
        flex-direction: column;
    }

    .table-number {
        font-size: 12px;
        font-weight: 700;
        color: var(--text-muted, #94A3B8);
    }

    .table-label {
        font-size: 17px;
        font-weight: 800;
        color: var(--text-primary, #0F172A);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 140px;
    }

    .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 11px;
        font-weight: 700;
        padding: 3px 8px;
        border-radius: 6px;
    }

    .pill-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
    }

    .pill-dot.green { background: var(--semantic-success, #059669); }
    .pill-dot.blue { background: var(--semantic-info, #2563EB); }
    .pill-dot.orange { background: var(--semantic-warning, #D97706); }

    .status-pill.status-available {
        background: #DCFCE7;
        color: #15803D;
        border: 1px solid rgba(21, 128, 61, 0.2);
    }

    .status-pill.status-occupied {
        background: #DBEAFE;
        color: #1D4ED8;
        border: 1px solid rgba(29, 78, 216, 0.2);
    }

    .status-pill.status-bill_requested {
        background: #FEF3C7;
        color: #B45309;
        border: 1px solid rgba(180, 83, 9, 0.25);
    }

    .empty-table-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 20px 0;
        color: var(--text-muted, #94A3B8);
        gap: 6px;
        cursor: pointer;
        border-radius: var(--radius-md, 10px);
        transition: background 0.15s ease;
    }

    .empty-table-state:hover {
        background: var(--neutral-surface-hover, #F8FAFC);
        color: var(--brand-primary, #FF6A00);
    }

    .table-icon-empty {
        opacity: 0.5;
        transition: transform 0.15s ease, opacity 0.15s ease;
    }

    .empty-table-state:hover .table-icon-empty {
        transform: scale(1.08);
        opacity: 0.9;
    }

    .capacity-text {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 12px;
        font-weight: 600;
        color: var(--text-secondary, #64748B);
    }

    .open-hint {
        font-size: 11px;
        font-weight: 700;
        color: var(--brand-primary, #FF6A00);
        opacity: 0;
        transition: opacity 0.15s ease;
    }

    .empty-table-state:hover .open-hint {
        opacity: 1;
    }

    .occupied-details {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 4px 0;
    }

    .occupied-client-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
    }

    .client-name {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 13px;
        color: var(--text-primary, #0F172A);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 170px;
    }

    .people-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        font-weight: 600;
        color: var(--text-secondary, #64748B);
        background: #F1F5F9;
        padding: 2px 6px;
        border-radius: 4px;
        flex-shrink: 0;
    }

    .occupied-meta-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 12px;
        color: var(--text-secondary, #64748B);
    }

    .time-elapsed {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: var(--brand-primary, #FF6A00);
        font-weight: 600;
        font-size: 12px;
    }

    .items-count-tag {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: var(--text-secondary, #64748B);
        font-size: 11.5px;
    }

    .bill-requested-banner {
        display: flex;
        align-items: center;
        gap: 6px;
        background: var(--semantic-warning-soft, #FFFBEB);
        color: var(--semantic-warning, #D97706);
        border: 1px solid rgba(217, 119, 6, 0.25);
        padding: 4px 8px;
        border-radius: 6px;
        font-size: 11.5px;
        font-weight: 700;
    }

    .bill-requested-banner.ready {
        background: #ECFDF5;
        color: #065F46;
        border-color: #A7F3D0;
    }

    .bill-requested-banner.in-prep {
        background: #EFF6FF;
        color: #1E40AF;
        border-color: #BFDBFE;
    }

    .table-total-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 4px;
        padding-top: 6px;
        border-top: 1px dashed var(--neutral-border, #E2E8F0);
    }

    .total-label {
        font-size: 12px;
        font-weight: 600;
        color: var(--text-muted, #64748B);
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .total-amount {
        font-size: 16px;
        font-weight: 900;
        color: var(--text-primary, #0F172A);
    }

    .table-card.status-occupied .total-amount,
    .table-card.status-bill_requested .total-amount {
        color: var(--brand-primary, #FF6A00);
    }

    .table-card-footer {
        display: flex;
        gap: 8px;
        margin-top: 12px;
    }

    .btn-card-action {
        flex: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 8px 12px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
        border: none;
    }

    .btn-card-action.open-btn {
        background: var(--brand-primary-soft, #FFF7ED);
        color: var(--brand-primary, #FF6A00);
        border: 1px solid rgba(255, 106, 0, 0.25);
    }

    .btn-card-action.open-btn:hover {
        background: var(--brand-primary, #FF6A00);
        color: #FFFFFF;
        border-color: var(--brand-primary, #FF6A00);
        transform: translateY(-1px);
    }

    .btn-card-action.view-btn {
        background: var(--brand-primary, #FF6A00);
        color: #FFFFFF;
    }

    .btn-card-action.view-btn:hover {
        background: var(--brand-primary-hover, #E85A00);
        transform: translateY(-1px);
    }

    .btn-card-action.pay-btn {
        background: #EA580C;
        color: #FFFFFF;
        flex: 0 0 85px;
    }

    .btn-card-action.pay-btn:hover {
        background: #C2410C;
        transform: translateY(-1px);
    }

    .btn-card-delete {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: #FFFFFF;
        color: var(--text-muted, #94A3B8);
        border: 1px solid var(--neutral-border, #CBD5E1);
        border-radius: var(--radius-md, 10px);
        padding: 0 10px;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .btn-card-delete:hover {
        background: #FEF2F2;
        color: var(--semantic-danger, #DC2626);
        border-color: #FECACA;
    }

    @media (max-width: 767.98px) {
        .table-card {
            min-height: 105px !important;
            padding: 9px 9px 8px 9px !important;
            border-radius: 12px !important;
            gap: 4px !important;
            box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06) !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: space-between !important;
            user-select: none;
            -webkit-tap-highlight-color: transparent;
        }

        .table-card:active {
            transform: scale(0.985);
        }

        .btn-card-delete {
            display: none !important;
        }

        .table-card-header {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            margin-bottom: 2px !important;
        }

        .table-number-group {
            display: flex !important;
            flex-direction: row !important;
            align-items: baseline !important;
            gap: 3px !important;
        }

        .table-number {
            font-size: 16px !important;
            font-weight: 800 !important;
            color: #0F172A !important;
            line-height: 1.2 !important;
        }

        .table-label {
            display: none !important;
        }

        .status-pill {
            font-size: 0 !important;
            line-height: 0 !important;
            padding: 0 !important;
            background: transparent !important;
            border: none !important;
            width: 14px !important;
            height: 14px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
        }

        .pill-dot {
            width: 9px !important;
            height: 9px !important;
            border-radius: 50% !important;
            box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9) !important;
        }

        .table-card.status-available {
            background: #F0FDF4 !important;
            border: 1.5px solid #86EFAC !important;
            box-shadow: 0 2px 6px rgba(34, 197, 94, 0.08) !important;
        }

        .table-card.status-available .open-btn {
            background: #16A34A !important;
            color: #FFFFFF !important;
            border: none !important;
        }

        .table-card.status-occupied {
            background: #EFF6FF !important;
            border: 1.5px solid #93C5FD !important;
            box-shadow: 0 2px 6px rgba(59, 130, 246, 0.08) !important;
        }

        .table-card.status-bill_requested {
            background: #FFFBEB !important;
            border: 1.5px solid #FCD34D !important;
            box-shadow: 0 2px 8px rgba(245, 158, 11, 0.14) !important;
        }

        .table-card.status-bill_requested .pay-btn {
            background: #D97706 !important;
            color: #FFFFFF !important;
            border: none !important;
        }

        .empty-table-state {
            padding: 2px 0 !important;
            gap: 2px !important;
        }

        .table-icon-empty {
            display: none !important;
        }

        .capacity-text {
            font-size: 11px !important;
            color: #64748B !important;
            gap: 4px !important;
        }

        .open-hint {
            display: none !important;
        }

        .occupied-details {
            gap: 3px !important;
            padding: 0 !important;
        }

        .occupied-client-row {
            display: none !important;
        }

        .occupied-meta-row {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            font-size: 11px !important;
            color: #475569 !important;
        }

        .time-elapsed {
            font-size: 11px !important;
            font-weight: 700 !important;
            color: #475569 !important;
            display: inline-flex !important;
            align-items: center !important;
            gap: 3px !important;
        }

        .items-count-tag {
            font-size: 10px !important;
            color: #64748B !important;
            display: inline-flex !important;
            align-items: center !important;
            gap: 3px !important;
        }

        .bill-requested-banner {
            padding: 2px 5px !important;
            font-size: 9.5px !important;
            font-weight: 700 !important;
            gap: 3px !important;
            margin: 2px 0 !important;
            border-radius: 4px !important;
            line-height: 1.2 !important;
        }

        .table-total-row {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            margin-top: 1px !important;
            padding-top: 2px !important;
            border-top: 1px dashed #E2E8F0 !important;
        }

        .total-label {
            font-size: 10px !important;
            color: #64748B !important;
        }

        .total-amount {
            font-size: 13px !important;
            font-weight: 800 !important;
            color: #0F172A !important;
        }

        .table-card-footer {
            margin-top: 4px !important;
            display: flex !important;
            gap: 5px !important;
        }

        .btn-card-action {
            height: 36px !important;
            min-height: 36px !important;
            font-size: 11.5px !important;
            font-weight: 700 !important;
            border-radius: 8px !important;
            padding: 0 4px !important;
            gap: 4px !important;
            touch-action: manipulation;
        }
    }
</style>
